import PropTypes from "prop-types";
import React from "react";
import EACheckbox from "../components/EACheckbox";
import {formatDate} from "./DateUtils";

export const NUMBERFORMAT = new Intl.NumberFormat("de-DE");

/**
 * Erstellt eine Column
 * @param id
 * @param label
 * @param style Style-Objekt, welches auf die HeaderCell angewendet wird
 * @returns {{id: *, label: *}}
 */
function createColumn(id, label = "", style = {}) {
    return {id, label, style};
}

createColumn.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    style: PropTypes.object,
};

/**
 * Erstellt eine Column zur Darstellung einer Zahl
 * @param id
 * @param label
 * @param style Style-Objekt, welches auf die HeaderCell angewendet wird
 * @returns {{id: *, label: *}}
 */
function createNumberColumn(id, label = "", style = {}) {
    let sortValueExtractor = (row) => row[id] ? row[id] : null;
    return createCalculatedColumn(id, (row) => (row[id] ? NUMBERFORMAT.format(row[id]) : null), label, style, sortValueExtractor);
}

createNumberColumn.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    style: PropTypes.object,
};

/**
 * Erstellt eine Column zur Darstellung eines Datums
 * @param id
 * @param label
 * @param style Style-Objekt, welches auf die HeaderCell angewendet wird
 * @returns {{id: *, label: *}}
 */
function createDateColumn(id, label = "", style = {}) {
    return createCalculatedColumn(
        id,
        (row) => (row[id] ? formatDate(new Date(row[id])) : null),
        label,
        style,
        (row) => {
            return row[id] ? new Date(row[id]) : row[id];
        }
    );
}

createDateColumn.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    style: PropTypes.object,
};

/**
 * Erstellt eine Column, welche den Inhalt im Body berechnet
 * @param id
 * @param calculateFunction Funktion, um anhand es Objekts der Zeile einen Wert zu berechnen.
 * @param label
 * @param style Style-Objekt, welches auf die HeaderCell angewendet wird
 * @param orderValueExtractorFunction Funktion, über welche der Wert aus der Zeile extrahiert werden kann,
 * welcher zum Vergleich bei der Sortierung herangezogen werden soll
 * @returns {{id: *, label: *}}
 */
function createCalculatedColumn(id, calculateFunction, label = "", style = {}, orderValueExtractorFunction = null) {
    const orderValueExtractor = {};
    if (orderValueExtractorFunction) {
        orderValueExtractor.orderValueExtractor = orderValueExtractorFunction;
    } else {
        // wenn keine Funktion für den Sortierwert angegeben ist, die Darstellende Funktion benutzen.
        // Dadurch ist der Column per Default Alphabetisch sortiert
        orderValueExtractor.orderValueExtractor = calculateFunction;
    }
    return {id, calculateFunction, label, style, ...orderValueExtractor};
}

createColumn.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    calculateFunction: PropTypes.func,
    numeric: PropTypes.bool,
    style: PropTypes.object,
};

/**
 *
 * @param id
 * @param componentProvider Funktion, welche die in der Zeile darzustellende Komponente erstellt, erhält als Parameter die Zeile selbst sowie den Index
 * @param label das anzuzeigende Label
 * @param headerComponentProvider Komponente, welche im Header dargestellt werden soll
 * @param style Style-Objekt, welches auf die HeaderCell angewendet wird
 * @returns {{id: *, label: string, componentProvider: *, headerComponentProvider: *}}
 */
function createComponentColumn(id, componentProvider, label = "", headerComponentProvider = null, style = {}) {
    return {id, label, componentProvider, headerComponentProvider, style};
}

createComponentColumn.propTypes = {
    id: PropTypes.string.isRequired,
    componentProvider: PropTypes.func.isRequired,
    label: PropTypes.string,
    headerComponentProvider: PropTypes.func,
    style: PropTypes.object,
};

/**
 * Erstellt eine Spalte, um die gesamte Zeile zu selektieren
 * @param tableId
 * @param idColumnIdentifier der Identifier des übergebenen Zeilenobjekts, welcher eindeutig die Zeile identifizieren kann
 * @param data
 * @param selectedItems
 * @param setSelectedItems
 * @returns {{id: *, label: string, componentProvider: *, headerComponentProvider: *}}
 */
function createSelectRowComponentColumn(tableId, idColumnIdentifier, data, selectedItems, setSelectedItems) {
    data = data ? data : [];
    return createComponentColumn(
        {tableId} + "-select-col",
        (row) => {
            return (
                <EACheckbox
                    id={"select-" + row[idColumnIdentifier]}
                    checked={isSelected(selectedItems, row[idColumnIdentifier])}
                    onChange={(event) => handleSelection(event, row[idColumnIdentifier], selectedItems, setSelectedItems, row)}
                />
            );
        },
        null,
        () => (
            <EACheckbox
                id={"select-all"}
                indeterminate={selectedItems.length > 0 && selectedItems.length < data.length}
                checked={data.length > 0 && selectedItems.length === data.length}
                onChange={(event) => handleSelectAllClick(event, data, idColumnIdentifier, setSelectedItems)}
            />
        ),
        {width: "30px", padding: "0 0 0 4px"}
    );
}

/**
 * Erstellt eine Spalte, um die gesamte Zeile zu selektieren für paginierte Tabellen
 * @param tableId
 * @param idColumnIdentifier der Identifier des übergebenen Zeilenobjekts, welcher eindeutig die Zeile identifizieren kann
 * @param selectedItems
 * @param setSelectedItems
 * @returns {{id: *, label: string, componentProvider: *, headerComponentProvider: *}}
 */
function createPaginatedSelectRowComponentColumn(tableId, idColumnIdentifier, selectedItems, setSelectedItems) {
    function onSelectAll(selected) {
        if (containsSelectAll(selected)) {
            setSelectedItems([]);
        } else {
            setSelectedItems(['ALL']);
        }
    }

    const handlePaginationSelection = (event, id, _selectedItems, _setSelectedItems, row) => {
        if (containsSelectAll(_selectedItems)) {
            _setSelectedItems([id]);
        } else {
            handleSelection(event, id, _selectedItems, _setSelectedItems, row);
        }
    };

    return createComponentColumn(
        {tableId} + "-select-col",
        (row) => {
            return (
                <EACheckbox
                    id={"select-" + row[idColumnIdentifier]}
                    checked={containsSelectAll(selectedItems) || isSelected(selectedItems, row[idColumnIdentifier])}
                    onChange={(event) => handlePaginationSelection(event, row[idColumnIdentifier], selectedItems, setSelectedItems, row)}
                />
            );
        },
        null,
        () => (
            <EACheckbox
                id={"select-all"}
                checked={containsSelectAll(selectedItems)}
                onChange={() => onSelectAll(selectedItems)}
            />
        ),
        {width: "30px", padding: "0 0 0 4px"}
    );
}

function containsSelectAll(selected) {
    return selected.indexOf('ALL') > -1;
}


createSelectRowComponentColumn.propTypes = {
    id: PropTypes.string.isRequired,
    idColumnIdentifier: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    selectedItems: PropTypes.array.isRequired,
    setSelectedItems: PropTypes.func.isRequired,
};

const handleSelectAllClick = (event, data, idColumnIdentifier, setSelectedItems) => {
    if (event.target.checked) {
        const newSelecteds = data.map((b) => b[idColumnIdentifier]);
        setSelectedItems(newSelecteds);
        return;
    }
    setSelectedItems([]);
};

const isSelected = (selectedItems, name) => selectedItems.indexOf(name) !== -1;

const handleSelection = (event, id, selectedItems, setSelectedItems, row) => {
    const selectedIndex = selectedItems.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedItems, id);
    } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
        newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selectedItems.slice(0, selectedIndex), selectedItems.slice(selectedIndex + 1));
    }

    setSelectedItems(newSelected);
};

export {
    createColumn,
    createNumberColumn,
    createDateColumn,
    createCalculatedColumn,
    createComponentColumn,
    createSelectRowComponentColumn,
    createPaginatedSelectRowComponentColumn,
    containsSelectAll,
    isSelected,
};
